import { useMemo, useState, useRef, useEffect } from 'react';
import { X, Plus, Info, ChevronDown, ArrowUp, ArrowDown } from 'lucide-react';
import type { RuleQuestion } from './RuleBuilder';
import type { ProductCatalog } from '../services/api';
import {
  type Operator,
  OPERATOR_LABELS,
  operatorsForType,
  needsValue as operatorNeedsValue,
  needsRangeValue,
} from './ruleOperators';

// Reserved for an in-progress pattern-matching parent feature. Keeping the
// imports live so `noUnusedLocals` doesn't reject the file while the feature
// is under development. Removed once the pattern UI consumes them.
void ArrowUp; void ArrowDown;
void OPERATOR_LABELS; void operatorsForType; void operatorNeedsValue; void needsRangeValue;

// ─── Types ───────────────────────────────────────────────────────────────────

export type OptionSourceType = 'static' | 'from_question' | 'product_master';

/**
 * Options sourced from the project's Product Master instead of a matrix typed
 * into this task.
 *
 * The admin picks which rung of the catalogue this question asks for, and which
 * earlier question narrows it. Chaining Group → Product → Size → Colour builds
 * the same drill-down the matrix used to, except the values come from the
 * catalogue — so adding a SKU updates every task at once instead of needing each
 * one re-edited.
 *
 * `depends_on_question_id` doubles as the narrowing parent AND the dependency
 * edge the server's cycle detector already walks, so no new plumbing is needed
 * in ruleGraph.collectDependencies.
 */
/**
 * LEGACY. A per-question binding to the Product Master.
 *
 * Superseded by `tasks.product_mode`: the product is now identified ONCE for
 * the whole task and every question is answered against it. This type survives
 * only so questions authored before that change still hydrate and re-save with
 * their binding intact; nothing writes a new one.
 */
export interface ProductMasterSource {
  type: 'product_master';
  level: 'group' | 'product' | 'characteristic' | 'sku' | 'grid';
  /** Which characteristic's values to offer. Only when level is 'characteristic'. */
  characteristic?: string;
  /** Optional static pre-filter: only items under this group. */
  scope_group_id?: string | null;
  /** The question whose answer narrows this one. Required for characteristic/sku. */
  depends_on_question_id?: string | null;
}

/** The flattened catalogue the editor needs to populate its pickers — the same
 *  shape the API's product-master catalogue endpoint returns. */
export type EditorProductCatalog = ProductCatalog;

type MappingRowType =
  | 'text'
  | 'number'
  | 'single_choice'
  | 'multiple_choice'
  | 'dropdown'
  | 'date'
  | 'photo'
  // 'skip' tells the runtime to hide this question entirely when the parent's
  // answer matches the row. Used for (parent, answer) branches where the
  // admin simply doesn't want to ask this question — e.g. Category = Surface
  // Laptop doesn't need the SKU drill-down that other categories use.
  | 'skip';

// One row of the cascading-options matrix. `type` dictates which input control
// the mobile will render when the composite parent answer matches;
// `options` is meaningful only when `type` is a choice type.
interface MappingEntry {
  type: MappingRowType;
  options?: string[];
}

// Canonical shape: `depends_on_question_ids` is the authoritative list of
// parent questions this question's options/type correlate with (in order).
// `depends_on_question_id` is kept as a back-compat mirror of the first
// parent so older mobile readers that only understand single-parent shape
// still render single-parent tasks.
//
// Each parent contributes its own independent rows (OR semantics): at
// runtime the first answered parent in the list decides the resolved answer.
// How those rows are shaped depends on the parent's question_type:
//
//   Choice parent (single_choice / multiple_choice / dropdown):
//     One row per parent answer, stored in `mapping` keyed by
//     `JSON.stringify([parent_id, answer])` — a parent-scoped 2-tuple.
//
//   Text / Number parent:
//     An ordered list of PatternRule entries stored in
//     `patterns_by_parent[parent_id]`. At runtime patterns are evaluated
//     top-to-bottom; the first match wins. If none match, the corresponding
//     entry in `default_by_parent[parent_id]` is used (or the question
//     hides if no default is configured).
interface PatternRule {
  operator: Operator;
  value?: string;
  value_to?: string;       // only for `between`
  entry: MappingEntry;
}

export interface OptionSource {
  depends_on_question_id: string;
  depends_on_question_ids?: string[];
  mapping: Record<string, MappingEntry>;                  // choice parents
  patterns_by_parent?: Record<string, PatternRule[]>;      // text/number parents
  default_by_parent?: Record<string, MappingEntry>;        // fallback per text/number parent
}

// Build a parent-scoped mapping key: one row per (parent question, parent
// answer) pair. A single question can correlate with multiple parents; each
// parent contributes its own independent set of rows (OR semantics, NOT a
// cartesian AND). At runtime the mobile resolver iterates the parents in
// order and the first matching rule wins.
//
// Key format is a JSON-stringified 2-tuple `["<parent_id>","<answer>"]` so
// separator collisions with user-entered values are impossible.
function parentScopedKey(parentId: string, answer: string): string {
  return JSON.stringify([parentId, answer]);
}

// Inverse of parentScopedKey. Returns null for shapes we don't recognise
// (lets the caller fall back to legacy bare-string interpretation).
function parseParentScopedKey(key: string): { parentId: string; answer: string } | null {
  if (typeof key !== 'string' || key.length === 0 || key[0] !== '[') return null;
  try {
    const parsed = JSON.parse(key);
    if (Array.isArray(parsed) && parsed.length === 2
        && typeof parsed[0] === 'string' && typeof parsed[1] === 'string') {
      return { parentId: parsed[0], answer: parsed[1] };
    }
  } catch {
    // Fall through.
  }
  return null;
}

// Normalize any stored option_source (old or new shape) into the canonical
// new shape so the editor and resolvers only have to reason about one form.
//   Old shape: `depends_on_question_id` (singular) + `mapping` keyed by bare
//              answer strings. The bare keys are re-keyed as `[parent, key]`.
//   New shape: `depends_on_question_ids` (plural) + `mapping` keyed by
//              `JSON.stringify([parent_id, answer])`.
// An intermediate cartesian-product shape (JSON arrays of answers, no parent
// ids) produced by an earlier in-flight iteration of this feature is treated
// as unmigrateable and dropped — those rows reset to defaults.
export function normalizeOptionSource(src: OptionSource | null | undefined): OptionSource | null {
  if (!src) return null;
  const ids = Array.isArray(src.depends_on_question_ids) && src.depends_on_question_ids.length > 0
    ? src.depends_on_question_ids
    : (src.depends_on_question_id ? [src.depends_on_question_id] : []);
  if (ids.length === 0) return null;
  const rawMapping = src.mapping || {};
  const mapping: Record<string, MappingEntry> = {};
  Object.entries(rawMapping).forEach(([k, v]) => {
    const parsed = parseParentScopedKey(k);
    if (parsed) {
      // Already parent-scoped — keep only if the parent is still in the list
      // (admins may have removed a parent since last save).
      if (ids.includes(parsed.parentId)) {
        mapping[k] = v as MappingEntry;
      }
      return;
    }
    // Bare legacy key (single-parent mapping written before multi support).
    // Re-key as parent-scoped against the first (and only) parent.
    if (ids.length === 1) {
      mapping[parentScopedKey(ids[0], k)] = v as MappingEntry;
    }
    // Otherwise drop silently — either an intermediate cartesian key or a
    // malformed entry; admin rebuilds via the editor.
  });
  // Carry forward per-parent pattern state, but only for parents still in
  // the current parent list — drops entries for parents the admin removed
  // since last save.
  const rawPatterns = src.patterns_by_parent || {};
  const patterns_by_parent: Record<string, PatternRule[]> = {};
  Object.entries(rawPatterns).forEach(([pid, list]) => {
    if (!ids.includes(pid)) return;
    if (!Array.isArray(list)) return;
    patterns_by_parent[pid] = list.filter(
      (p) => !!p && typeof p === 'object' && typeof (p as any).operator === 'string'
    );
  });
  const rawDefaults = src.default_by_parent || {};
  const default_by_parent: Record<string, MappingEntry> = {};
  Object.entries(rawDefaults).forEach(([pid, entry]) => {
    if (!ids.includes(pid)) return;
    default_by_parent[pid] = normalizeMappingEntry(entry, 'text');
  });
  return {
    depends_on_question_id: ids[0],
    depends_on_question_ids: ids,
    mapping,
    patterns_by_parent,
    default_by_parent,
  };
}

const MAPPING_ROW_TYPES: { value: MappingRowType; label: string }[] = [
  { value: 'skip', label: "Skip (don't ask)" },
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'date', label: 'Date' },
  { value: 'photo', label: 'Photo' },
];

const CHOICE_ROW_TYPES: MappingRowType[] = ['single_choice', 'multiple_choice', 'dropdown'];

// Back-compat coercion: accept either the legacy string[] shape (type falls
// back to the parent question's question_type) or the new {type, options?}
// shape and always produce the normalized object form.
function normalizeMappingEntry(raw: unknown, parentType: MappingRowType): MappingEntry {
  if (Array.isArray(raw)) {
    return { type: parentType, options: raw.filter((x): x is string => typeof x === 'string') };
  }
  if (raw && typeof raw === 'object' && 'type' in (raw as any) && typeof (raw as any).type === 'string') {
    const entry = raw as MappingEntry;
    return {
      type: entry.type,
      options: Array.isArray(entry.options) ? entry.options : undefined,
    };
  }
  return { type: parentType };
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  sourceType: OptionSourceType;
  onSourceTypeChange: (t: OptionSourceType) => void;

  // From-question state (only used when sourceType === 'from_question')
  source: OptionSource | null;
  onSourceChange: (next: OptionSource | null) => void;

  // Available questions this one can pull its options from
  availableQuestions: RuleQuestion[];
  currentQuestionId?: string | null;

  // The current question's global question_type — used as the default per-row
  // type for newly introduced mapping rows and as the back-compat fallback
  // when reading legacy array-shaped rows.
  currentQuestionType?: MappingRowType;

}

// Derive the enumerable answer set a question can produce at runtime.
// - Static-options question: its own `options` array.
// - Dynamic-options question (its options come from another question via
//   `option_source.mapping`): the union of options across all choice-type
//   mapping rows, since those are the only branches that produce an
//   enumerable answer space. Non-choice rows (text/number/date/photo) yield
//   free-form answers that cannot be mapped discretely, so they are skipped.
// Used so a question whose options are dynamic can still act as the parent
// for another question's dependency.
const CHOICE_TYPES_SET = new Set(['single_choice', 'multiple_choice', 'dropdown']);
function getEffectiveOptions(q: RuleQuestion): string[] {
  if (Array.isArray(q.options) && q.options.length > 0) return q.options as string[];
  const mapping = q.option_source?.mapping;
  if (!mapping) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  // Each mapping value carries the target answer config. The mapping KEY
  // shape differs between single-parent (bare string) and multi-parent
  // (JSON-array string) — but we only need the VALUES here to compute the
  // enumerable answer set this question can produce, so the key shape is
  // irrelevant to this function.
  Object.values(mapping).forEach((raw) => {
    let opts: string[] = [];
    if (Array.isArray(raw)) {
      // Legacy shape: string[] of options, inheriting the parent's global type.
      opts = raw.filter((o): o is string => typeof o === 'string');
    } else if (raw && typeof raw === 'object' && 'type' in (raw as any)) {
      const entry = raw as { type: string; options?: string[] };
      if (CHOICE_TYPES_SET.has(entry.type) && Array.isArray(entry.options)) {
        opts = entry.options.filter((o): o is string => typeof o === 'string');
      }
    }
    opts.forEach((o) => {
      const trimmed = o.trim();
      if (trimmed && !seen.has(trimmed)) {
        seen.add(trimmed);
        out.push(trimmed);
      }
    });
  });
  return out;
}

export default function OptionSourceEditor({
  sourceType,
  onSourceTypeChange,
  source,
  onSourceChange,
  availableQuestions,
  currentQuestionId,
  currentQuestionType = 'dropdown',
}: Props) {
  // Eligible parents: choice-type questions (enumerable answers drive the
  // per-answer mapping grid) OR text/number questions (unbounded answers,
  // matched via pattern rules). A choice question with no effective options
  // is still filtered out — we can't render rows for a parent that has no
  // answers to branch on yet.
  const candidateSources = useMemo(
    () => availableQuestions.filter((q) => {
      if (q.id === currentQuestionId) return false;
      if (q.question_type === 'text' || q.question_type === 'number') return true;
      if (['single_choice', 'multiple_choice', 'dropdown'].includes(q.question_type)) {
        return getEffectiveOptions(q).length > 0;
      }
      return false;
    }),
    [availableQuestions, currentQuestionId]
  );

  // Normalize incoming `source` so the editor always works with the canonical
  // multi-parent shape (parent-scoped mapping keys). Avoids dual-shape
  // branching below.
  const normalizedSource = useMemo(() => normalizeOptionSource(source), [source]);
  const parentIds = normalizedSource?.depends_on_question_ids || [];
  const parentQs = parentIds
    .map((id) => candidateSources.find((q) => q.id === id))
    .filter((q): q is RuleQuestion => !!q);

  // Rebuild the choice-mapping for a new parent list: one row per
  // (parent, parent's option) for choice parents only. Text/number parents
  // contribute no entries here — their state lives in `patterns_by_parent`
  // / `default_by_parent`. Preserves rows whose (parent, answer) pair is
  // still present; drops rows for removed parents; seeds new rows for
  // newly added choice parents with the current question's type.
  const rebuildMapping = (
    nextParentIds: string[],
    prior: Record<string, MappingEntry>
  ): Record<string, MappingEntry> => {
    const next: Record<string, MappingEntry> = {};
    nextParentIds.forEach((pid) => {
      const pq = candidateSources.find((q) => q.id === pid);
      if (!pq) return;
      if (pq.question_type === 'text' || pq.question_type === 'number') return;
      getEffectiveOptions(pq).forEach((ans) => {
        const key = parentScopedKey(pid, ans);
        const existing = prior[key];
        next[key] = normalizeMappingEntry(existing, currentQuestionType);
      });
    });
    return next;
  };

  // Keep only per-parent pattern/default state for parents still in the list.
  const prunePerParent = <T,>(
    prior: Record<string, T> | undefined,
    ids: string[],
  ): Record<string, T> => {
    const next: Record<string, T> = {};
    if (!prior) return next;
    ids.forEach((pid) => {
      if (pid in prior) next[pid] = prior[pid];
    });
    return next;
  };

  const addParent = (qId: string) => {
    if (parentIds.includes(qId)) return;
    const newParentIds = [...parentIds, qId];
    const newParent = candidateSources.find((q) => q.id === qId);
    const isPatternParent = newParent?.question_type === 'text' || newParent?.question_type === 'number';
    const nextPatterns = prunePerParent(normalizedSource?.patterns_by_parent, newParentIds);
    const nextDefaults = prunePerParent(normalizedSource?.default_by_parent, newParentIds);
    if (isPatternParent && !nextPatterns[qId]) {
      // Seed one empty pattern row so the editor is immediately usable.
      nextPatterns[qId] = [{ operator: 'equals', value: '', entry: { type: currentQuestionType } }];
    }
    onSourceChange({
      depends_on_question_id: newParentIds[0],
      depends_on_question_ids: newParentIds,
      mapping: rebuildMapping(newParentIds, normalizedSource?.mapping || {}),
      patterns_by_parent: nextPatterns,
      default_by_parent: nextDefaults,
    });
  };

  const removeParent = (qId: string) => {
    const newParentIds = parentIds.filter((id) => id !== qId);
    if (newParentIds.length === 0) {
      onSourceChange(null);
      return;
    }
    onSourceChange({
      depends_on_question_id: newParentIds[0],
      depends_on_question_ids: newParentIds,
      mapping: rebuildMapping(newParentIds, normalizedSource?.mapping || {}),
      patterns_by_parent: prunePerParent(normalizedSource?.patterns_by_parent, newParentIds),
      default_by_parent: prunePerParent(normalizedSource?.default_by_parent, newParentIds),
    });
  };

  const selectInitialParent = () => {
    // First time Depends mode is chosen and no source exists yet — seed with
    // the first eligible parent so the grid is immediately visible.
    if (!source && candidateSources.length > 0) {
      const first = candidateSources[0];
      const isPatternParent = first.question_type === 'text' || first.question_type === 'number';
      const patterns_by_parent: Record<string, PatternRule[]> = isPatternParent
        ? { [first.id]: [{ operator: 'equals', value: '', entry: { type: currentQuestionType } }] }
        : {};
      onSourceChange({
        depends_on_question_id: first.id,
        depends_on_question_ids: [first.id],
        mapping: rebuildMapping([first.id], {}),
        patterns_by_parent,
        default_by_parent: {},
      });
    }
  };

  // Mutation helpers for pattern-mode parents. All three produce a new
  // OptionSource with the patterns/default for a single parent id replaced.
  const setPatternsFor = (pid: string, patterns: PatternRule[]) => {
    if (!normalizedSource) return;
    const next = { ...(normalizedSource.patterns_by_parent || {}) };
    next[pid] = patterns;
    onSourceChange({ ...normalizedSource, patterns_by_parent: next });
  };
  const setDefaultFor = (pid: string, entry: MappingEntry | undefined) => {
    if (!normalizedSource) return;
    const next = { ...(normalizedSource.default_by_parent || {}) };
    if (entry) next[pid] = entry;
    else delete next[pid];
    onSourceChange({ ...normalizedSource, default_by_parent: next });
  };

  const availableToAdd = candidateSources.filter((q) => !parentIds.includes(q.id));

  return (
    <div className="space-y-3">
      {/* Source type selector */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onSourceTypeChange('static')}
          className={`flex-1 rounded-lg border-2 px-3 py-2 text-left transition-all ${
            sourceType === 'static'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <div className="text-xs font-semibold text-gray-900">Static list</div>
          <div className="mt-0.5 text-[11px] text-gray-500">Same options for everyone</div>
        </button>
        <button
          type="button"
          onClick={() => {
            onSourceTypeChange('from_question');
            selectInitialParent();
          }}
          disabled={candidateSources.length === 0}
          className={`flex-1 rounded-lg border-2 px-3 py-2 text-left transition-all ${
            sourceType === 'from_question'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          } ${candidateSources.length === 0 ? 'opacity-40 cursor-not-allowed' : ''}`}
        >
          <div className="text-xs font-semibold text-gray-900">Depends on another question</div>
          <div className="mt-0.5 text-[11px] text-gray-500">
            {candidateSources.length === 0 ? 'Add another question to this task first' : 'Options change based on that answer'}
          </div>
        </button>
      </div>

      {/* From-question editor */}
      {sourceType === 'from_question' && normalizedSource && (
        <div className="space-y-2.5">
          {/* Parent picker — multi-select chips. Pick one or more questions
              this question's answer should correlate with; the grid below
              auto-expands to every combination of their answers. */}
          <div className="flex items-start gap-2 flex-wrap">
            <span className="text-xs font-medium text-gray-600 mt-1.5 shrink-0">Options come from:</span>
            <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
              {parentQs.map((q) => (
                <span
                  key={q.id}
                  className="inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-xs text-primary-800"
                >
                  {q.question_text.length > 40 ? q.question_text.slice(0, 40) + '…' : q.question_text}
                  {parentIds.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeParent(q.id)}
                      className="text-primary-600 hover:text-red-600"
                      title="Remove this parent"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
              {availableToAdd.length > 0 && (
                <AddParentPicker available={availableToAdd} onPick={addParent} />
              )}
            </div>
          </div>

          {/* One group per parent question. For choice parents, the group
              lists the parent's possible answers — admin configures the
              target answer independently per (parent, answer) pair. For
              text/number parents, the group is a list of pattern rules
              (operator + value → target answer spec) evaluated in order,
              plus a default fallback. No AND-combinations across parents;
              at runtime whichever parent answer matches first decides. */}
          {parentQs.length > 0 && (
            <div className="space-y-3">
              {parentQs.map((parentQ, groupIdx) => {
                const isPatternParent = parentQ.question_type === 'text' || parentQ.question_type === 'number';
                if (isPatternParent) {
                  const patterns = normalizedSource.patterns_by_parent?.[parentQ.id] || [];
                  const defaultEntry = normalizedSource.default_by_parent?.[parentQ.id];
                  return (
                    <PatternRowsEditor
                      key={parentQ.id}
                      parentQ={parentQ}
                      groupIdx={groupIdx}
                      patterns={patterns}
                      defaultEntry={defaultEntry}
                      currentQuestionType={currentQuestionType}
                      onPatternsChange={(next) => setPatternsFor(parentQ.id, next)}
                      onDefaultChange={(next) => setDefaultFor(parentQ.id, next)}
                    />
                  );
                }
                const opts = getEffectiveOptions(parentQ);
                if (opts.length === 0) return null;
                return (
                  <div key={parentQ.id} className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
                    <div className="grid grid-cols-[1.2fr_2fr] bg-gray-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-600 items-center">
                      <div>
                        When "{parentQ.question_text.slice(0, 30)}{parentQ.question_text.length > 30 ? '…' : ''}" is…
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span>This question's answer:</span>
                        {groupIdx > 0 && (
                          <span className="text-[10px] font-normal normal-case text-gray-500 italic">
                            checked if earlier parents unanswered
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {opts.map((ans) => {
                        const key = parentScopedKey(parentQ.id, ans);
                        const entry = normalizeMappingEntry(normalizedSource.mapping[key], currentQuestionType);
                        return (
                          <MappingRow
                            key={key}
                            sourceAnswer={ans}
                            entry={entry}
                            onChange={(next) => {
                              onSourceChange({
                                ...normalizedSource,
                                mapping: { ...normalizedSource.mapping, [key]: next },
                              });
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-[11px] text-gray-500 flex items-start gap-1.5">
            <Info className="h-3 w-3 flex-shrink-0 mt-0.5 text-gray-400" />
            Each parent contributes its own rows — no AND-combinations. At
            runtime, parents are checked in the order listed above; whichever
            parent is answered first decides this question's answer. Leave a
            row's options empty to hide this question when that answer is
            picked.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── AddParentPicker ─────────────────────────────────────────────────────────
// Lightweight dropdown for adding another parent question to the correlation
// list. Closes on outside click, on pick, or on escape.

function AddParentPicker({
  available,
  onPick,
}: {
  available: RuleQuestion[];
  onPick: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-primary-300 bg-white px-2 py-0.5 text-xs text-primary-700 hover:bg-primary-50"
      >
        <Plus className="h-3 w-3" /> Add question
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-64 max-h-56 overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {available.map((q) => (
            <button
              key={q.id}
              type="button"
              onClick={() => { onPick(q.id); setOpen(false); }}
              className="block w-full text-left px-3 py-1.5 text-xs text-gray-800 hover:bg-primary-50"
            >
              {q.question_text.length > 60 ? q.question_text.slice(0, 60) + '…' : q.question_text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PatternRowsEditor (for text/number parents) ─────────────────────────────
// Renders the pattern-matching UI when the parent question is text or number.
// Each row is a PatternRule: (operator, value[, value_to]) → MappingEntry.
// Patterns are evaluated top-to-bottom at runtime; the first match decides.
// A mandatory "Default (no match)" row at the bottom is bound to
// `default_by_parent[parent.id]` and used when no pattern matches.

function PatternRowsEditor({
  parentQ,
  groupIdx,
  patterns,
  defaultEntry,
  currentQuestionType,
  onPatternsChange,
  onDefaultChange,
}: {
  parentQ: RuleQuestion;
  groupIdx: number;
  patterns: PatternRule[];
  defaultEntry: MappingEntry | undefined;
  currentQuestionType: MappingRowType;
  onPatternsChange: (next: PatternRule[]) => void;
  onDefaultChange: (next: MappingEntry | undefined) => void;
}) {
  const operators = operatorsForType(parentQ.question_type);
  const isNumber = parentQ.question_type === 'number';

  const updateAt = (idx: number, next: PatternRule) => {
    const copy = patterns.slice();
    copy[idx] = next;
    onPatternsChange(copy);
  };
  const removeAt = (idx: number) => {
    const copy = patterns.slice();
    copy.splice(idx, 1);
    onPatternsChange(copy);
  };
  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const copy = patterns.slice();
    [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
    onPatternsChange(copy);
  };
  const moveDown = (idx: number) => {
    if (idx === patterns.length - 1) return;
    const copy = patterns.slice();
    [copy[idx + 1], copy[idx]] = [copy[idx], copy[idx + 1]];
    onPatternsChange(copy);
  };
  const addRow = () => {
    onPatternsChange([
      ...patterns,
      { operator: operators[0], value: '', entry: { type: currentQuestionType } },
    ]);
  };

  const setOperator = (idx: number, op: Operator) => {
    const current = patterns[idx];
    const next: PatternRule = {
      ...current,
      operator: op,
      value: operatorNeedsValue(op) ? current.value ?? '' : undefined,
    };
    if (needsRangeValue(op)) next.value_to = current.value_to ?? '';
    else delete next.value_to;
    updateAt(idx, next);
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
      <div className="flex items-center justify-between bg-gray-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
        <div>
          When "{parentQ.question_text.slice(0, 30)}{parentQ.question_text.length > 30 ? '…' : ''}" answer…
        </div>
        {groupIdx > 0 && (
          <span className="text-[10px] font-normal normal-case text-gray-500 italic">
            checked if earlier parents unanswered
          </span>
        )}
      </div>

      <div className="divide-y divide-gray-200">
        {patterns.map((p, idx) => (
          <div key={idx} className="grid grid-cols-[1fr_2fr] gap-3 px-3 py-2.5 items-start">
            <div className="flex flex-wrap items-center gap-1.5">
              <select
                value={p.operator}
                onChange={(e) => setOperator(idx, e.target.value as Operator)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {operators.map((op) => (
                  <option key={op} value={op}>{OPERATOR_LABELS[op]}</option>
                ))}
              </select>
              {operatorNeedsValue(p.operator) && (
                <>
                  <input
                    type={isNumber ? 'number' : 'text'}
                    value={p.value ?? ''}
                    onChange={(e) => updateAt(idx, { ...p, value: e.target.value })}
                    placeholder={needsRangeValue(p.operator) ? 'min' : 'value'}
                    className="w-24 rounded border border-gray-200 px-1.5 py-1 text-xs text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  {needsRangeValue(p.operator) && (
                    <>
                      <span className="text-[11px] text-gray-500">to</span>
                      <input
                        type={isNumber ? 'number' : 'text'}
                        value={p.value_to ?? ''}
                        onChange={(e) => updateAt(idx, { ...p, value_to: e.target.value })}
                        placeholder="max"
                        className="w-24 rounded border border-gray-200 px-1.5 py-1 text-xs text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </>
                  )}
                </>
              )}
              <div className="flex items-center gap-0.5 ml-auto">
                <button
                  type="button"
                  onClick={() => moveUp(idx)}
                  disabled={idx === 0}
                  className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move up"
                >
                  <ArrowUp className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => moveDown(idx)}
                  disabled={idx === patterns.length - 1}
                  className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Move down"
                >
                  <ArrowDown className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={() => removeAt(idx)}
                  className="p-1 text-gray-400 hover:text-red-600"
                  title="Remove pattern"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
            <EntryTypeEditor
              entry={p.entry}
              onChange={(next) => updateAt(idx, { ...p, entry: next })}
            />
          </div>
        ))}
        <div className="grid grid-cols-[1fr_2fr] gap-3 px-3 py-2.5 items-start bg-white/60">
          <div className="text-xs italic text-gray-600 self-center">
            Default (no pattern matched)
          </div>
          <EntryTypeEditor
            entry={defaultEntry ?? { type: currentQuestionType }}
            onChange={(next) => onDefaultChange(next)}
          />
        </div>
      </div>

      <div className="px-3 py-2 bg-gray-100 border-t border-gray-200">
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1 rounded-md border border-dashed border-primary-300 bg-white px-2 py-0.5 text-xs text-primary-700 hover:bg-primary-50"
        >
          <Plus className="h-3 w-3" /> Add pattern
        </button>
      </div>
    </div>
  );
}

// ─── StaticOptionsEditor ─────────────────────────────────────────────────────

export function StaticOptionsEditor({
  options,
  onChange,
}: {
  options: string[];
  onChange: (next: string[]) => void;
}) {
  const addOption = () => onChange([...options, '']);
  const removeOption = (idx: number) => {
    const next = options.slice();
    next.splice(idx, 1);
    onChange(next.length > 0 ? next : ['']);
  };
  const updateOption = (idx: number, v: string) => {
    const next = options.slice();
    next[idx] = v;
    onChange(next);
  };

  return (
    <div className="space-y-1.5">
      {options.map((opt, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span className="text-xs text-gray-400 w-4">{idx + 1}.</span>
          <input
            type="text"
            value={opt}
            onChange={(e) => updateOption(idx, e.target.value)}
            className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder={`Option ${idx + 1}`}
          />
          {options.length > 1 && (
            <button
              type="button"
              onClick={() => removeOption(idx)}
              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={addOption}
        className="w-full py-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded border border-dashed border-primary-300 transition-colors"
      >
        + Add option
      </button>
    </div>
  );
}

// ─── MappingRow (single row in the matrix editor) ────────────────────────────

const TYPE_LABELS: Record<MappingRowType, string> = {
  text: 'Free text answer',
  number: 'Numeric answer',
  single_choice: '',        // choice types render the options editor
  multiple_choice: '',
  dropdown: '',
  date: 'Date answer',
  photo: 'Photo answer',
  skip: 'Question is hidden for this answer',
};

// Right-side of a mapping row — the entry type selector + options editor
// (for choice types) or type-label (for free-form types). Used by both the
// choice-parent MappingRow (which adds its own left cell showing the parent
// answer) and the text/number PatternRowsEditor (which renders its own
// left cell showing the pattern's operator + value).
function EntryTypeEditor({
  entry,
  onChange,
}: {
  entry: MappingEntry;
  onChange: (next: MappingEntry) => void;
}) {
  const isChoice = CHOICE_ROW_TYPES.includes(entry.type);
  const opts = entry.options || [];

  const changeType = (next: MappingRowType) => {
    if (CHOICE_ROW_TYPES.includes(next)) {
      onChange({ type: next, options: opts });
    } else {
      onChange({ type: next });
    }
  };

  const setOptions = (nextOpts: string[]) => onChange({ type: entry.type, options: nextOpts });
  const addChip = () => setOptions([...opts, '']);
  const removeChip = (idx: number) => {
    const n = opts.slice();
    n.splice(idx, 1);
    setOptions(n);
  };
  const updateChip = (idx: number, v: string) => {
    const n = opts.slice();
    n[idx] = v;
    setOptions(n);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={entry.type}
        onChange={(e) => changeType(e.target.value as MappingRowType)}
        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
      >
        {MAPPING_ROW_TYPES.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>
      {isChoice ? (
        <>
          {opts.map((opt, idx) => (
            <div
              key={idx}
              className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs"
            >
              <input
                type="text"
                value={opt}
                onChange={(e) => updateChip(idx, e.target.value)}
                className="w-20 bg-transparent focus:outline-none"
                placeholder="option"
              />
              <button
                type="button"
                onClick={() => removeChip(idx)}
                className="text-gray-400 hover:text-red-500"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addChip}
            className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-primary-300 bg-primary-50 px-2 py-0.5 text-xs text-primary-700 hover:bg-primary-100"
          >
            <Plus className="h-3 w-3" /> add
          </button>
        </>
      ) : (
        <span className="text-[11px] italic text-gray-500">{TYPE_LABELS[entry.type]}</span>
      )}
    </div>
  );
}

function MappingRow({
  sourceAnswer,
  entry,
  onChange,
}: {
  sourceAnswer: React.ReactNode;
  entry: MappingEntry;
  onChange: (next: MappingEntry) => void;
}) {
  return (
    <div className="grid grid-cols-[1fr_2fr] gap-3 px-3 py-2.5">
      <div className="text-xs font-medium text-gray-700 truncate self-center">{sourceAnswer}</div>
      <EntryTypeEditor entry={entry} onChange={onChange} />
    </div>
  );
}
